// src/components/admin/AdminLayout.js
import React, { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth }  from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const NAV = [
  { to: '/admin',           label: 'Dashboard',  icon: '📊', end: true  },
  { to: '/admin/products',  label: 'Products',   icon: '🎆', end: false },
  { to: '/admin/orders',    label: 'Orders',     icon: '📦', end: false },
  { to: '/admin/coupons',   label: 'Coupons',    icon: '🎟️',  end: false },
];

function SidebarContent({ onClose }) {
  const { user, logout } = useAuth();
  const { toast }        = useToast();
  const navigate         = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out.');
    navigate('/');
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-xl font-semibold text-sm
     transition-all duration-150 cursor-pointer
     ${isActive
       ? 'bg-red-600 text-white shadow-md shadow-red-600/30'
       : 'text-gray-500 hover:bg-red-50 hover:text-red-700'}`;

  return (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-5 py-6 border-b border-gray-100">
        <Link to="/" className="flex items-center gap-3 group">
          <img src="/logo.png" alt="Lakshmi Crackers"
            className="h-14 w-14 object-contain group-hover:scale-110 transition-transform duration-200"
            style={{ filter: 'drop-shadow(0 0 8px rgba(245,200,66,0.6))' }}/>
          <div>
            <p className="font-black text-red-700 text-base leading-tight">Lakshmi Crackers</p>
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest">Admin Panel</p>
          </div>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map(item => (
          <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}
            onClick={onClose}>
            <span className="text-xl w-6 text-center">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
        <div className="pt-3 border-t border-gray-100 mt-3">
          <Link to="/" className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-semibold text-sm
            text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all`}>
            <span className="text-xl w-6 text-center">🏠</span> Storefront
          </Link>
        </div>
      </nav>

      {/* User section */}
      <div className="px-3 py-4 border-t border-gray-100">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center
            text-red-700 font-black text-sm flex-shrink-0">
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-gray-800 text-sm truncate">{user?.name}</p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={handleLogout}
          className="w-full flex items-center gap-2 px-4 py-2 rounded-xl text-sm
            font-semibold text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors">
          <span>🚪</span> Log Out
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout({ children, title }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-100 shadow-sm flex-shrink-0">
        <SidebarContent onClose={() => {}} />
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-72 bg-white shadow-2xl flex flex-col h-full">
            <SidebarContent onClose={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center gap-4 flex-shrink-0">
          <button className="lg:hidden text-gray-500 hover:text-gray-700 transition-colors"
            onClick={() => setSidebarOpen(true)}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
          <h1 className="text-xl font-black text-gray-800 flex-1">{title}</h1>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
